/**
 * AT-04 — Attribution Diagnostics section rendered inside `RawData.tsx`.
 *
 * Read-only view of `attributionDeliveryLog[]` entries pulled from the
 * extension via `AttributionContext`. No approve / reject / retry / edit
 * controls — Settings owns disclosure preferences, RawData owns debug
 * inspection.
 *
 * `ack_4xx_epoch_closed_deferred` entries surface a Slice-2-deferral hint
 * matching the AT-04 acceptance copy: *"deferred until next epoch (Slice 2 feature)"*.
 */

import type { DeliveryLogEntry } from '../services/attribution-bridge';

export interface AttributionDiagnosticsProps {
  deliveryLog: DeliveryLogEntry[];
}

function truncateHex(hex: string, head = 8, tail = 4): string {
  if (hex.length <= head + tail + 2) return hex;
  return `${hex.slice(0, head + 2)}…${hex.slice(-tail)}`;
}

function formatTimestamp(ms: number): string {
  try {
    return new Date(ms).toISOString();
  } catch {
    return String(ms);
  }
}

function deferralHintFor(state: string): string | null {
  if (state === 'ack_4xx_epoch_closed_deferred') {
    return 'deferred until next epoch (Slice 2 feature)';
  }
  return null;
}

export function AttributionDiagnostics({ deliveryLog }: AttributionDiagnosticsProps) {
  return (
    <section
      aria-labelledby="attribution-diagnostics-heading"
      data-testid="attribution-diagnostics"
      className="mt-8"
    >
      <h2
        id="attribution-diagnostics-heading"
        className="text-lg font-semibold mb-2"
      >
        Attribution Diagnostics
      </h2>
      <p className="text-xs text-gray-500 mb-4">
        Read-only view of recent ad-delivery events your wallet sent. Settings
        owns disclosure preferences; this section is for debugging.
      </p>

      {deliveryLog.length === 0 ? (
        <p
          data-testid="attribution-diagnostics-empty"
          className="text-sm text-gray-500"
        >
          No deliveries yet.
        </p>
      ) : (
        <ul
          data-testid="attribution-diagnostics-list"
          className="space-y-2"
        >
          {deliveryLog.map((entry) => (
            <li
              key={entry.delivery_event_id}
              data-testid="attribution-diagnostics-row"
              className="rounded border border-gray-200 p-3"
            >
              <div className="flex justify-between text-xs text-gray-500">
                <span>{formatTimestamp(entry.recorded_at)}</span>
                <span data-testid="row-state">{entry.state}</span>
              </div>
              <dl className="mt-1 text-sm">
                <div>
                  <dt className="inline text-gray-500">Campaign:</dt>{' '}
                  <dd className="inline" data-testid="row-campaign-ref">
                    {truncateHex(entry.campaign_ref)}
                  </dd>
                </div>
                <div>
                  <dt className="inline text-gray-500">Epoch:</dt>{' '}
                  <dd className="inline" data-testid="row-rotation-counter">
                    {entry.rotation_counter}
                  </dd>
                </div>
                {entry.publisher_origin && (
                  <div>
                    <dt className="inline text-gray-500">Publisher:</dt>{' '}
                    <dd className="inline" data-testid="row-publisher-origin">
                      {entry.publisher_origin}
                    </dd>
                  </div>
                )}
                {entry.ack_status !== undefined && (
                  <div>
                    <dt className="inline text-gray-500">Ack status:</dt>{' '}
                    <dd className="inline" data-testid="row-ack-status">
                      {entry.ack_status}
                    </dd>
                  </div>
                )}
                {entry.ack_reason && (
                  <div>
                    <dt className="inline text-gray-500">Ack reason:</dt>{' '}
                    <dd className="inline" data-testid="row-ack-reason">
                      {entry.ack_reason}
                    </dd>
                  </div>
                )}
                {entry.payment_reference && (
                  <div>
                    <dt className="inline text-gray-500">Payment ref:</dt>{' '}
                    <dd className="inline" data-testid="row-payment-ref">
                      {truncateHex(entry.payment_reference)}
                    </dd>
                  </div>
                )}
              </dl>
              {deferralHintFor(entry.state) && (
                <p
                  data-testid="row-deferred-hint"
                  className="mt-2 text-xs italic text-amber-700"
                >
                  {deferralHintFor(entry.state)}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
